/**
 * Honest, succinct upgrade / limit copy shared across molecule.dev's limit-hit and
 * upgrade surfaces (mvp CST3).
 *
 * Used by the in-chat `upgrade_prompt` / `build_degraded` cards (chat-custom-events.ts) —
 * the IN-CHAT surface the daily AI-cost block emits (chat-handler.ts) — the proactive
 * {@link UsageNudge}, and the HTTP limit-error notifications (bonds/app-http.ts).
 *
 * {@link honestLimitNote} is two short sentences (AGENTS.md rule 13 — user-facing copy
 * stays short, no marketing speak; exact wording per the user, 2026-07-06): open source
 * + the mission, then the honest why — inference costs real money. No dark patterns: it
 * is only shown alongside a real limit/upgrade prompt, and it never spins the limit as
 * anything but the plain truth.
 *
 * i18n posture: like the sibling Synthase chat-card prose (`chat.onboarding.*` — the
 * models-intro and pre-alpha cards), the candor copy lives behind `t()` with an English
 * `defaultValue` that IS the real text (the sanctioned `@molecule/app-i18n` pattern), not
 * in the typed 74-language `UiTranslations` table — a locale bond may still override
 * `upgrade.honestNote` / `upgrade.viewOnGithub`. Keep the English wording verbatim; it is
 * the user's candor bar.
 *
 * @module
 */

import { t } from '@molecule/app-i18n'

import { SUPPORT_URL } from './branding.js'

/** Public Molecule open-source repository, linked from the honest upgrade copy. */
export const MOLECULE_REPO_URL = 'https://github.com/molecule-dev/molecule'

/**
 * The honest note shown with any limit / upgrade prompt: open source + the mission,
 * then the honest reason there are limits (inference costs real money). Localized via
 * `t()` with the verbatim English default.
 *
 * @returns The localized honest note.
 */
export function honestLimitNote(): string {
  return t('upgrade.honestNote', undefined, {
    defaultValue:
      'Molecule is open source — designed to build real software more reliably and at a ' +
      "lower cost. We'd love to give everyone unlimited AI, but inference costs real money.",
  })
}

/**
 * Label for the action / link that opens the public Molecule repository alongside the
 * honest note (so the "free + open source" claim is verifiable). Localized via `t()`.
 *
 * @returns The localized GitHub link label.
 */
export function githubRepoLinkLabel(): string {
  return t('upgrade.viewOnGithub', undefined, { defaultValue: 'View on GitHub' })
}

/**
 * Label for the guest "Sign up" action. A guest can't Upgrade without an account —
 * signing up is what lifts their cap — so guest limit surfaces lead with this instead
 * of an Upgrade button. Shares the `guest.reminder.signUp` key so the wording stays in
 * sync with the guest-reminder card. Localized via `t()`.
 *
 * @returns The localized Sign up label.
 */
export function signUpLabel(): string {
  return t('guest.reminder.signUp', undefined, { defaultValue: 'Sign up' })
}

// ---------------------------------------------------------------------------
// Daily-limit reset framing (mvp CST4)
// ---------------------------------------------------------------------------
//
// The daily AI budget exists to bring users back, not to wall them out — so the
// limit-hit surfaces (toast / in-chat card / nudge) should LEAD with WHEN the
// budget refreshes ("in ~Xh" or "tomorrow"), with the upgrade as a secondary
// option. These helpers turn the structured `resetAt` (next UTC midnight,
// computed server-side and threaded through `buildLimitError`) into warm,
// time-zone-aware copy, localized via `t()` with the verbatim English default.

/**
 * Human countdown to the next allowance reset given its absolute timestamp.
 *
 * - elapsed / unknown → `"soon"`
 * - under an hour away → `"in under an hour"`
 * - a day or more out → `"tomorrow"` (≤ 1 day) or `"in about N days"`
 * - lands on a later LOCAL calendar day and is ≥ 6h out → `"tomorrow"` (reads
 *   more naturally than "in about 17 hours")
 * - otherwise → `"in about N hours"` (or `"in about 1 hour"`)
 *
 * The day scale is what makes this usable for a BILLING-PERIOD allowance, which
 * can be weeks out. Without it every reset past ~6h collapsed to "tomorrow", so
 * a paid user three weeks from their renewal was told their allowance came back
 * in the morning.
 *
 * Pure and time-zone-aware (uses the viewer's local day boundary for the
 * "tomorrow" decision); the server-rendered fallback in `tiers.ts` is coarser.
 *
 * @param resetAt - Epoch-ms of the next reset (the payload's `resetAt`).
 * @param now - Reference time in epoch ms (defaults to `Date.now()`).
 * @returns A localized human countdown phrase.
 */
export function formatResetCountdown(resetAt: number, now: number = Date.now()): string {
  const diffMs = resetAt - now
  if (!Number.isFinite(diffMs) || diffMs <= 0) {
    return t('upgrade.reset.soon', undefined, { defaultValue: 'soon' })
  }
  const hours = diffMs / 3_600_000
  if (hours < 1) {
    return t('upgrade.reset.underHour', undefined, { defaultValue: 'in under an hour' })
  }
  if (hours >= 24) {
    const days = Math.round(hours / 24)
    if (days <= 1) {
      return t('upgrade.reset.tomorrow', undefined, { defaultValue: 'tomorrow' })
    }
    return t(
      'upgrade.reset.inDays',
      { days },
      { defaultValue: `in about ${days} ${days === 1 ? 'day' : 'days'}` },
    )
  }
  const rounded = Math.round(hours)
  const laterLocalDay = new Date(resetAt).toDateString() !== new Date(now).toDateString()
  if (laterLocalDay && rounded >= 6) {
    return t('upgrade.reset.tomorrow', undefined, { defaultValue: 'tomorrow' })
  }
  return t(
    'upgrade.reset.inHours',
    { hours: rounded },
    { defaultValue: `in about ${rounded} ${rounded === 1 ? 'hour' : 'hours'}` },
  )
}

/**
 * A short "who this allowance is for" phrase, so a daily-budget line names the tier
 * the user is on — making WHY they ran out explicit. Mirrors the server-side
 * `tierAudienceLabel` (tiers.ts). Empty string for an unknown/missing tier (so the
 * copy stays clean rather than appending a half-phrase).
 *
 * @param category - The user's tier ('anonymous' | 'free' | 'pro' | 'team'), or undefined.
 * @returns e.g. `' for guests'`, `' for free accounts'` (leading space), or `''`.
 */
export function tierAudienceSuffix(category: string | undefined): string {
  switch (category) {
    case 'anonymous':
      return ' for guests'
    case 'free':
      return ' for free accounts'
    case 'pro':
      return ' on the Pro plan'
    case 'team':
      return ' on the Team plan'
    default:
      return ''
  }
}

/**
 * The AI-allowance window a tier meters over, as it arrives on the wire
 * (`aiMeter.window`, `limits.aiBudget.window`, the `ai_cost` limit payload's
 * `allowanceWindow`, and `/cost`'s `allowanceWindow`).
 *
 * `undefined` means an API that predates the field — callers fall back to the
 * daily framing that shipped before it rather than promising a month they
 * cannot confirm.
 */
export type AllowanceWindow = 'daily' | 'billing-period'

/** A daily allowance resets within a day; a reset further away than this is a billing period. */
const PERIOD_INFERENCE_MS = 48 * 3_600_000

/**
 * A reset instant as a short local calendar date ("October 16").
 *
 * @param resetAt - Epoch-ms of the reset.
 * @returns The date in the viewer's locale.
 */
export function formatResetDate(resetAt: number): string {
  return new Date(resetAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
}

/**
 * The noun phrase for an allowance window, for any surface that has to NAME the
 * allowance rather than just count down to it ("You're close to your **monthly
 * AI allowance** limit").
 *
 * "Monthly" rather than "this billing period": it is true for a yearly
 * subscriber too — the allowance refreshes monthly on the renewal day, it is
 * not an annual pot — and it is the word a buyer actually uses. One source for
 * this phrase, so the nudge, the inspector and the meters cannot drift into
 * three different names for one thing.
 *
 * @param window - The tier's allowance window, or undefined on an older API.
 * @returns e.g. `'monthly AI allowance'` / `'daily AI allowance'`.
 */
export function allowanceLabel(window: AllowanceWindow | undefined): string {
  return window === 'billing-period'
    ? t('usage.metric.aiCostMonthly', undefined, { defaultValue: 'monthly AI allowance' })
    : t('usage.metric.aiCost', undefined, { defaultValue: 'daily AI allowance' })
}

/**
 * PRIMARY allowance-limit copy (CST4): one reset-first sentence that names WHEN
 * the allowance comes back — the limit's whole purpose. The upgrade option is
 * the secondary action, not this line. When
 * `resetAt` is missing it falls back to a generic reset framing (still
 * reset-first, never Upgrade-first). When `category` is supplied it also names the
 * tier ("…budget for guests…") so the user knows WHICH allowance they hit. For the
 * guest tier it folds in ", or you can log in for more free AI" so guests know
 * signing in lifts the cap.
 *
 * `window` decides whether this is a DAY or a MONTH. Only the unpaid tiers reset
 * daily; a paid tier meters over its billing period, and calling that "today's
 * free AI budget" told a paying user both the wrong span and the wrong plan.
 *
 * @param resetAt - Epoch-ms of the next reset, or undefined.
 * @param now - Reference time in epoch ms (defaults to `Date.now()`).
 * @param category - The user's tier category, to name the allowance. Optional.
 * When `window` is absent (an older API, or a payload that dropped it) the
 * reset distance decides: a daily allowance always resets within a day, so a
 * `resetAt` more than 48 hours away can only be a billing period. Defaulting
 * to "today's" there told a Pro user their budget "resets in about 12 days".
 *
 * @param resetAt - Epoch-ms of the next reset, or undefined.
 * @param now - Reference time in epoch ms (defaults to `Date.now()`).
 * @param category - The user's tier category, to name the allowance. Optional.
 * @param window - The tier's allowance window; omit to infer it from `resetAt`.
 * @returns The localized primary reset line.
 */
export function allowanceResetLine(
  resetAt: number | undefined,
  now: number = Date.now(),
  category?: string,
  window?: AllowanceWindow,
): string {
  const who = tierAudienceSuffix(category)
  const effectiveWindow: AllowanceWindow | undefined =
    window ??
    (resetAt != null && Number.isFinite(resetAt) && resetAt - now > PERIOD_INFERENCE_MS
      ? 'billing-period'
      : undefined)
  if (effectiveWindow === 'billing-period') {
    // A paid allowance is neither "today's" nor "free" — the user is paying for
    // it, and it refreshes monthly. No guest clause: guests are never here.
    if (resetAt == null || !Number.isFinite(resetAt) || resetAt <= now) {
      return t(
        'upgrade.reset.linePeriodGeneric',
        { who },
        {
          defaultValue: `You've used this month's AI allowance${who}. It refreshes each billing period.`,
        },
      )
    }
    const date = formatResetDate(resetAt)
    return t(
      'upgrade.reset.linePeriodDate',
      { date, who },
      {
        defaultValue: `You've used this billing period's AI allowance${who}. It resets on ${date}.`,
      },
    )
  }
  // Guests can lift this cap by signing in — a real account carries a larger free
  // AI budget than a guest session — so fold that in as a comma clause on the reset
  // sentence (the trailing period is added AFTER `more`) to tell them WHY the Sign
  // up / Log in action helps. Guests only: free/paid tiers already have the bigger
  // budget, so it would be false there.
  const more =
    category === 'anonymous'
      ? t('upgrade.reset.guestMore', undefined, {
          defaultValue: ', or you can log in for more free AI',
        })
      : ''
  if (resetAt == null || !Number.isFinite(resetAt) || resetAt <= now) {
    return t(
      'upgrade.reset.lineGeneric',
      { who, more },
      {
        defaultValue: `You've used today's free AI budget${who}. It resets daily${more}.`,
      },
    )
  }
  const when = formatResetCountdown(resetAt, now)
  return t(
    'upgrade.reset.line',
    { when, who, more },
    {
      defaultValue: `You've used today's free AI budget${who}. It resets ${when}${more}.`,
    },
  )
}

/**
 * Copy for the returning-session "budget reset" banner (CST4): a positive note,
 * shown when a user who previously hit the daily cap comes back after it has
 * refreshed, confirming they have the full free budget again. Localized via
 * `t()`.
 *
 * @returns The localized welcome-back line.
 */
export function budgetResetWelcomeBack(): string {
  return t('upgrade.reset.welcomeBack', undefined, {
    defaultValue:
      'Welcome back! Your free daily AI budget has refreshed and you can pick up where you left off.',
  })
}

// ---------------------------------------------------------------------------
// Spend-cap framing (docs/BAAS-pricing.md §3 — the trust rail)
// ---------------------------------------------------------------------------
//
// `limitType: 'spend_cap'` (429) / the `upgrade_prompt` card with
// `feature: 'spend_cap'` mean the user's OWN monthly extra-usage cap was
// reached — the trust rail worked exactly as promised ("we pause, we never
// surprise-bill"). These surfaces must therefore be ACTIONABLE ("raise your
// cap in Billing"), never an upgrade pitch and never the daily-reset framing.

/** Route of the Billing page, where the spend-cap control lives. */
export const BILLING_ROUTE = '/billing'

/**
 * Label for the action that takes the user to the Billing page's spend-cap
 * control after their extra-usage cap paused metering. Localized via `t()`.
 *
 * @returns The localized raise-your-cap action label.
 */
export function raiseSpendCapLabel(): string {
  return t('upgrade.spendCap.raise', undefined, { defaultValue: 'Raise spend cap' })
}

/**
 * Fallback line for a spend-cap pause when no server message is available:
 * names WHAT paused (their own monthly cap) and how to resume — deliberately
 * NOT an upgrade pitch (the card's button carries the Billing route). Localized
 * via `t()`.
 *
 * @returns The localized spend-cap-reached line.
 */
export function spendCapReachedLine(): string {
  return t('upgrade.spendCap.reached', undefined, {
    defaultValue:
      "You've hit your monthly spend cap. AI usage is paused until you raise it or the next billing period starts.",
  })
}

/**
 * Label for the action that takes the user to the Billing page's add-funds
 * control after an empty prepaid balance paused metered usage. Distinct from
 * {@link raiseSpendCapLabel}: that user chose a ceiling and hit it, this one
 * needs money in the account. Localized via `t()`.
 *
 * @returns The localized add-funds action label.
 */
export function addFundsLabel(): string {
  return t('upgrade.balance.addFunds', undefined, { defaultValue: 'Add funds' })
}

/**
 * Fallback line for an empty prepaid balance when no server message is
 * available. One plain sentence, one action — never an upgrade pitch, since the
 * user is already on a paid plan. Localized via `t()`.
 *
 * @returns The localized empty-balance line.
 */
export function balanceEmptyLine(): string {
  return t('upgrade.balance.empty', undefined, {
    defaultValue: 'Your usage balance is empty. Add funds in Billing to keep building.',
  })
}

/**
 * The spend-cap pause as a teammate sees it: the cap is the project OWNER's,
 * and this viewer cannot raise it, so "raise it in Billing" would be wrong.
 *
 * @returns The localized one-line notice.
 */
export function ownerSpendCapReachedLine(): string {
  return t('upgrade.spendCap.reachedOwner', undefined, {
    defaultValue:
      "The project owner's monthly spend cap has been reached. Only the owner can change billing.",
  })
}

/**
 * The empty-balance pause as a teammate sees it (see {@link ownerSpendCapReachedLine}).
 *
 * @returns The localized one-line notice.
 */
export function ownerBalanceEmptyLine(): string {
  return t('upgrade.balance.emptyOwner', undefined, {
    defaultValue: "The project owner's usage balance is empty. Only the owner can change billing.",
  })
}

// ---------------------------------------------------------------------------
// The one plan/usage CTA resolver (docs: the 2026-09-15 CTA consistency audit)
// ---------------------------------------------------------------------------
//
// Every plan/usage/balance surface used to decide its own button from whatever
// scrap it happened to hold, so the SAME fact produced different advice
// depending on where the user saw it: a paid user out of prepaid funds was told
// "Your usage balance is empty" by the chat card and offered "Upgrade" (into a
// plans modal that could not fix it) by the live limit banner beside it.
//
// The SERVER now decides ONE `billingAction` per limit and ships it on every
// 402/429/403 limit body, on the `upgrade_prompt` chat-card data, and on
// `GET /account/usage`. {@link billingCta} is the single client-side table that
// turns it into buttons, so every surface renders the same advice for the same
// state. {@link resolveBillingAction} carries the fallback derivation for cards
// PERSISTED before the server sent the field (and for older API responses).

/**
 * The server-decided next billing step for a limit — the vocabulary
 * {@link billingCta} renders.
 *
 * - `sign_up` — no account yet; signing up is what lifts the cap.
 * - `upgrade` — a plan cap with a higher tier to move to.
 * - `add_payment_method` — metered usage needs funds and there is no card on file.
 * - `add_funds` — card on file, prepaid balance empty.
 * - `raise_spend_cap` — the user's OWN monthly cap paused metering.
 * - `enable_extra_usage` — paid, allowance spent, extra usage never opted into.
 * - `contact_support` — a rail only support can lift (the per-period top-up
 *   velocity cap), where "add funds" is a dead end.
 * - `owner_only` — billing follows the PROJECT OWNER; this viewer cannot fix it.
 * - `none` — nothing the user can buy or change (e.g. the top tier at a cap).
 */
export type BillingActionKind =
  | 'sign_up'
  | 'upgrade'
  | 'add_payment_method'
  | 'add_funds'
  | 'raise_spend_cap'
  | 'enable_extra_usage'
  | 'contact_support'
  | 'owner_only'
  | 'none'

/** Every {@link BillingActionKind}, for validating a wire value. */
const BILLING_ACTION_KINDS = new Set<string>([
  'sign_up',
  'upgrade',
  'add_payment_method',
  'add_funds',
  'raise_spend_cap',
  'enable_extra_usage',
  'contact_support',
  'owner_only',
  'none',
])

/** Route of the plans / checkout page (intercepted into the in-place modal). */
export const PRICING_ROUTE = '/pricing'

/** Route of the sign-up page (intercepted into the in-place auth modal). */
export const SIGNUP_ROUTE = '/signup'

/** Route of the log-in page (intercepted into the in-place auth modal). */
export const LOGIN_ROUTE = '/login'

/**
 * One resolved CTA button.
 *
 * Deliberately a plain, self-contained shape rather than an import of any one
 * consumer's action type: it is structurally assignable to all three surfaces
 * that render it — the shared IDE's `ChatEventCardAction` (chat cards + the
 * ChatPanel limit banner), the app's `BannerAction` ({@link UsageNudge}, the
 * Dashboard create-error banner) and `NotificationAction` (the HTTP toast) —
 * so there is one table and no per-surface copy of it.
 */
export interface BillingCtaAction {
  /**
   * Stable identity of this button, so a consumer can keep its OWN established
   * `data-mol-id`s (e.g. the nudge's `usage-nudge-signup` / `usage-nudge-cta`)
   * without re-deriving them from the label, which is localized.
   */
  id:
    | 'sign-up'
    | 'log-in'
    | 'upgrade'
    | 'add-payment-method'
    | 'add-funds'
    | 'raise-spend-cap'
    | 'enable-extra-usage'
    | 'contact-support'
  /** Already-localized button label. */
  label: string
  /**
   * Link target. Always a plain href: `/pricing` and `/billing` are intercepted
   * by `AuthModalMount` and open the in-place modal, which an `onClick` +
   * `window.open` would escape.
   */
  href: string
  /** Canonical CTA color (molecule/DESIGN.md → "CTA color semantics"). */
  color: 'primary' | 'success'
  /** True when `href` leaves the app (the support mailto), so it opens out. */
  external?: boolean
}

/** What {@link billingCta} resolved for a limit: the buttons, and any extra line. */
export interface BillingCta {
  /** The action kind that was rendered, after any fallback derivation. */
  action: BillingActionKind
  /**
   * The buttons to render, or `undefined` when there is nothing to offer
   * (`owner_only`, `none`) — never an empty array, so a consumer can pass it
   * straight through to an optional `action`/`actions` prop.
   */
  actions: BillingCtaAction[] | undefined
  /**
   * A short line to append to the surface's copy, when the action alone cannot
   * say it — currently only `owner_only` ("Only the project owner can change
   * billing.").
   */
  note?: string
}

/** The context a surface knows about a limit it is rendering a CTA for. */
export interface BillingCtaInput {
  /** The server's decision. Anything unrecognized falls back to derivation. */
  billingAction?: string
  /** The limit that fired (`usage_balance`, `spend_cap`, `ai_cost`, …). */
  limitType?: string
  /** The server's "this user must sign up rather than upgrade" flag. */
  requiresSignup?: boolean
  /** The client's own auth state, for a surface the server flag does not cover. */
  isGuest?: boolean
  /**
   * The next tier up, as the server ships it. An EXPLICIT `null` means there is
   * none (the top tier) — the terminal state that must never show an Upgrade
   * button into a plans modal with nothing to sell. `undefined` = not stated.
   */
  upgradeTier?: string | null
}

/**
 * Label for the action that adds a card when metered usage has no payment
 * method behind it. Distinct from {@link addFundsLabel}: there is nothing to
 * charge yet. Localized via `t()`.
 *
 * @returns The localized add-a-payment-method label.
 */
export function addPaymentMethodLabel(): string {
  return t('upgrade.balance.addPaymentMethod', undefined, {
    defaultValue: 'Add a payment method',
  })
}

/**
 * Label for the action that opts a paid account into metered extra usage, so
 * work continues past the plan allowance. Localized via `t()`.
 *
 * @returns The localized enable-extra-usage label.
 */
export function enableExtraUsageLabel(): string {
  return t('upgrade.extraUsage.enable', undefined, { defaultValue: 'Enable extra usage' })
}

/**
 * Label for the action that reaches a human, for the one rail the user cannot
 * clear themselves (the per-period top-up velocity cap). Localized via `t()`.
 *
 * @returns The localized contact-support label.
 */
export function contactSupportLabel(): string {
  return t('upgrade.support.contact', undefined, { defaultValue: 'Contact support' })
}

/**
 * Label for the Upgrade action. One helper so the plan CTA cannot drift into
 * three different words again (`upgrade.viewPlans` resolves to "View plans" and
 * is NOT this action). Localized via `t()`.
 *
 * @returns The localized Upgrade label.
 */
export function upgradeLabel(): string {
  return t('upgrade.upgrade', undefined, { defaultValue: 'Upgrade' })
}

/**
 * Label for the guest "Log in" action, paired with {@link signUpLabel}. Shares
 * the `guest.reminder.logIn` key so the wording stays in sync with the
 * guest-reminder card. Localized via `t()`.
 *
 * @returns The localized Log in label.
 */
export function logInLabel(): string {
  return t('guest.reminder.logIn', undefined, { defaultValue: 'Log in' })
}

/**
 * The line shown to a teammate whose work is blocked by the PROJECT OWNER's
 * billing. Gating follows project ownership, so this viewer's own Billing page
 * cannot fix it and offering them one would be a dead end. Localized via `t()`.
 *
 * @returns The localized owner-only note.
 */
export function ownerOnlyBillingNote(): string {
  return t('upgrade.owner.note', undefined, {
    defaultValue: 'Only the project owner can change billing.',
  })
}

/**
 * Fallback line for metered usage with NO payment method behind it — the state
 * the product never had words for, so every surface told the user to "add
 * funds" through a rail that first needs a card. Localized via `t()`.
 *
 * @returns The localized no-payment-method line.
 */
export function noPaymentMethodLine(): string {
  return t('upgrade.balance.noPaymentMethod', undefined, {
    defaultValue: 'Your usage balance is empty and there is no payment method on file.',
  })
}

/**
 * Fallback line for the per-period top-up velocity rail, where "add funds" is a
 * dead end because the rail that refuses top-ups is the one that stopped them.
 * Localized via `t()`.
 *
 * @returns The localized velocity-cap line.
 */
export function topUpVelocityLine(): string {
  return t('upgrade.balance.velocityCap', undefined, {
    defaultValue: 'You have reached the top-up limit for this billing period.',
  })
}

/**
 * The one-sentence statement of WHY work stopped, for an action whose own state
 * differs from the surface's default copy.
 *
 * Returns `undefined` for the actions where the surface's existing line is
 * already correct — an allowance that ran out reads the same whether the next
 * step is Upgrade, Enable extra usage, or nothing at all; only the button
 * changes. This is what keeps a nudge from saying "You've used this month's AI
 * allowance… Upgrade" to a user whose real problem is an empty balance.
 *
 * @param action - The resolved action kind.
 * @returns The localized state line, or undefined to keep the surface's own.
 */
export function billingStateLine(action: BillingActionKind): string | undefined {
  switch (action) {
    case 'add_funds':
      return balanceEmptyLine()
    case 'add_payment_method':
      return noPaymentMethodLine()
    case 'raise_spend_cap':
      return spendCapReachedLine()
    case 'contact_support':
      return topUpVelocityLine()
    default:
      return undefined
  }
}

/**
 * Resolve the {@link BillingActionKind} for a limit.
 *
 * The server's `billingAction` wins whenever it is present and recognized. The
 * derivation below exists for the two cases where it is not: a chat card
 * PERSISTED before the field shipped, and an API build that predates it.
 *
 * @param input - What the surface knows about the limit.
 * @returns The action kind to render.
 */
export function resolveBillingAction(input: BillingCtaInput): BillingActionKind {
  const declared = input.billingAction
  if (typeof declared === 'string' && BILLING_ACTION_KINDS.has(declared)) {
    return declared as BillingActionKind
  }
  if (input.requiresSignup === true || input.isGuest === true) return 'sign_up'
  if (input.limitType === 'usage_balance') return 'add_funds'
  if (input.limitType === 'spend_cap') return 'raise_spend_cap'
  // An EXPLICIT null is the server saying "there is no tier above this one".
  // `undefined` only means the response did not state it, which is not the same
  // thing and must keep the ordinary Upgrade path.
  if (input.upgradeTier === null) return 'none'
  return 'upgrade'
}

/**
 * The one plan/usage CTA table: turn a limit's state into the button(s) that
 * actually fix it.
 *
 * Every plan/usage/balance surface renders through this — the IDE's live limit
 * banner, the `upgrade_prompt` chat cards, the HTTP limit toast, the proactive
 * usage nudge and the Dashboard create-error banner — so one state can never
 * again produce "Add funds" on one surface and "Upgrade" on the one beside it.
 *
 * Colors are the canonical CTA semantics (molecule/DESIGN.md): Sign up
 * `primary` beside Log in `success`; every single-button action `primary`.
 *
 * @param input - What the surface knows about the limit.
 * @returns The resolved action kind, its buttons, and any note to append.
 */
export function billingCta(input: BillingCtaInput): BillingCta {
  const action = resolveBillingAction(input)
  switch (action) {
    case 'sign_up':
      return {
        action,
        actions: [
          { id: 'sign-up', label: signUpLabel(), href: SIGNUP_ROUTE, color: 'primary' },
          { id: 'log-in', label: logInLabel(), href: LOGIN_ROUTE, color: 'success' },
        ],
      }
    case 'add_payment_method':
      return {
        action,
        actions: [
          {
            id: 'add-payment-method',
            label: addPaymentMethodLabel(),
            href: BILLING_ROUTE,
            color: 'primary',
          },
        ],
      }
    case 'add_funds':
      return {
        action,
        actions: [
          { id: 'add-funds', label: addFundsLabel(), href: BILLING_ROUTE, color: 'primary' },
        ],
      }
    case 'raise_spend_cap':
      return {
        action,
        actions: [
          {
            id: 'raise-spend-cap',
            label: raiseSpendCapLabel(),
            href: BILLING_ROUTE,
            color: 'primary',
          },
        ],
      }
    case 'enable_extra_usage':
      return {
        action,
        actions: [
          {
            id: 'enable-extra-usage',
            label: enableExtraUsageLabel(),
            href: BILLING_ROUTE,
            color: 'primary',
          },
        ],
      }
    case 'contact_support':
      return {
        action,
        actions: [
          {
            id: 'contact-support',
            label: contactSupportLabel(),
            href: SUPPORT_URL,
            color: 'primary',
            external: true,
          },
        ],
      }
    case 'owner_only':
      // Nothing to offer THIS viewer — say who can fix it instead of handing
      // them a Billing page that governs an account the limit does not follow.
      return { action, actions: undefined, note: ownerOnlyBillingNote() }
    case 'none':
      return { action, actions: undefined }
    default:
      return {
        action: 'upgrade',
        actions: [{ id: 'upgrade', label: upgradeLabel(), href: PRICING_ROUTE, color: 'primary' }],
      }
  }
}
